import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { all_routes } from '../../router/all_routes';
import { useDispatch, useSelector } from 'react-redux';
import { getApplicationModulesAPI } from '../../../core/data/redux/applicationModulesSlice';

interface ServiceItem {
    label: string;
    route: string;
    slug?: string;
    disabled?: boolean;
}

interface ServiceCategory {
    id: string;
    title: string;
    description: string;
    icon: string;
    slug?: string;
    items: ServiceItem[];
}

const SLUG_ALIASES: Record<string, string[]> = {
    all_leave_tab: ["leave_management_applications", "all_leave_tab"],
    leave_management_applications: ["all_leave_tab", "leave_management_applications"],
    reimbursement_claims_advances_all: ["reimbursement_claims_advances", "reimbursement_claims_advances_all"],
    reimbursement_claims_advances: ["reimbursement_claims_advances_all", "reimbursement_claims_advances"],
    employee_apar: ["employee_appraisal_performance_management", "employee_apar"],
    employee_appraisal_performance_management: ["employee_apar", "employee_appraisal_performance_management"],
};

const Services = () => {
    const routes = all_routes;
    const dispatch = useDispatch<any>();
    const [searchQuery, setSearchQuery] = useState('');

    const applicationModulesState = useSelector((state: any) => state.applicationModulesState || {});

    useEffect(() => {
        if (!applicationModulesState?.data && (!applicationModulesState?.loading || applicationModulesState?.loading === "idle")) {
            dispatch(getApplicationModulesAPI({ page: 1, limit: 200, orderColumn: "name", orderBy: "ASC" }));
        }
    }, [dispatch, applicationModulesState]);

    const rawModules = applicationModulesState?.data;
    let modulesArray: any[] = [];
    if (Array.isArray(rawModules)) {
        modulesArray = rawModules;
    } else if (rawModules && Array.isArray(rawModules.data)) {
        modulesArray = rawModules.data;
    }

    const activeSlugs = React.useMemo(() => {
        if (!modulesArray || modulesArray.length === 0) return null;
        return new Set(
            modulesArray
                .filter((mod: any) => mod.status === true)
                .map((mod: any) => mod.slug)
        );
    }, [modulesArray]);

    const isSlugActive = (slug: string) => {
        if (!activeSlugs) return true; // If modules not loaded, keep visible
        if (activeSlugs.has(slug)) return true;
        const aliases = SLUG_ALIASES[slug];
        if (aliases) {
            return aliases.some((alias) => activeSlugs.has(alias));
        }
        return false;
    };

    const serviceCategories: ServiceCategory[] = [
        {
            id: 'id-management',
            title: 'Employee Data & Identity Management',
            description: 'Apply for fresh identity cards, manage dependent inclusions and deletions.',
            icon: 'ti ti-id',
            slug: 'employee_data_identity_management',
            items: [
                {
                    label: 'Application for new I-Card',
                    route: routes.idCard.list,
                    slug: 'application_for_new_icard'
                },
                {
                    label: 'Inclusion of dependent parents including widow mother',
                    route: routes.idCard.inclusionOfDependent,
                    slug: 'inclusion_of_dependent_parents_including_widow_mother'
                },
                {
                    label: 'Deletion of dependent parents including widow mother',
                    route: routes.idCard.deletionOfDependent,
                    slug: 'deletion_of_dependent_parents_including_widow_mother'
                },
                {
                    label: 'List of Inclusion / Deletion of family members (Spouse and Children)',
                    route: routes.idCard.familyMemberInclusion,
                    slug: 'list_of_inclusion_deletion_of_family_members_spouse_children'
                }
            ]
        },
        {
            id: 'it-sap-services',
            title: 'IT & SAP Access Management',
            description: 'Apply for SAP roles, server authorization, T-Codes and IT asset requisitions.',
            icon: 'ti ti-lock-access',
            items: [
                {
                    label: 'SAP Authorization Request',
                    route: routes.sapAuthorization.list,
                    slug: 'sap_authorization_form'
                },
                {
                    label: 'New SAP Authorization',
                    route: routes.sapAuthorization.create,
                    slug: 'sap_authorization_form'
                },
                {
                    label: 'E-Requisition (IT Equipment)',
                    route: routes.eRequisitionIT.list,
                    slug: 'e_requisition_it'
                }
            ]
        },
        {
            id: 'leave-attendance',
            title: 'Leave & Attendance',
            description: 'Apply for leaves, check attendance status and manage shift regularizations.',
            icon: 'ti ti-calendar-user',
            slug: 'all_leave_tab',
            items: [
                {
                    label: 'Leave Applications',
                    route: '#'
                },
                {
                    label: 'Attendance Regularization',
                    route: '#'
                }
            ]
        },
        {
            id: 'medical-leave-encashment',
            title: 'Medical & Leave Encashment',
            description: 'Apply for leave encashment of encashable EL, and IVF / Para 6.2 / Para 6.3 prior approval.',
            icon: 'ti ti-heartbeat',
            slug: 'employee_medical_leave_encashment',
            items: [
                {
                    label: 'Leave Encashment for EL beyond 60 leave',
                    route: routes.leaveEncashment.list,
                    slug: 'leave_encashment_for_el_beyond_60_leave'
                },
                {
                    label: 'Prior Approval for IVF, Para 6.2/6.3',
                    route: routes.medicalAttendance.list,
                    slug: 'prior_approval_for_ivf_para_6_2_6_3_treatment'
                }
            ]
        },
        {
            id: 'health-welfare',
            title: 'Employee Health & Welfare Applications',
            description: 'Apply for annual comprehensive health checkup authority letter and related welfare requests.',
            icon: 'ti ti-heart',
            slug: 'employee_health_welfare_applications',
            items: [
                {
                    label: 'Authority Letter for Annual Comprehensive Health Checkup',
                    route: routes.authorityLetterHealthCheckup.list,
                    slug: 'authority_letter_for_annual_comprehensive_health_checkup'
                },
                {
                    label: 'Staff Welfare Fund Grants',
                    route: routes.staffWelfareFund.list,
                    slug: 'staff_welfare_fund_grants'
                }
            ]
        },
        {
            id: 'tax-loan',
            title: 'Tax & Loan Management',
            description: 'View income tax card details and check loan surety received or given status.',
            icon: 'ti ti-receipt-tax',
            slug: 'employee_tax_and_loan_management',
            items: [
                {
                    label: 'Display Tax Card',
                    route: routes.displayTaxCard,
                    slug: 'display_tax_card'
                },
                {
                    label: 'Tax Simplifier Statement',
                    route: routes.taxSimplifier,
                    slug: 'tax_simplifier_statement'
                },
                {
                    label: 'Loan Surety Status Check',
                    route: routes.loanSurety,
                    slug: 'loan_surety_status_check'
                }
            ]
        },
        {
            id: 'financial-retirement',
            title: 'Financial & Retirement Benefits',
            description: 'View yearly NPS/superannuation contributions and annual PF ledger details.',
            icon: 'ti ti-coin',
            slug: 'employee_financial_retirement_benefits',
            items: [
                {
                    label: 'Annual NPS/Superannuation Detail',
                    route: routes.annualNps,
                    slug: 'annual_nps_superannuation_application'
                },
                {
                    label: 'Annual PF Details',
                    route: routes.annualPf,
                    slug: 'annual_pf_details'
                }
            ]
        },
        {
            id: 'transfer-posting',
            title: 'Employee Transfer & Posting Module',
            description: 'Self Transfer applications and cadre movement.',
            icon: 'ti ti-arrows-transfer-down',
            slug: 'employee_transfer_posting_module',
            items: [
                {
                    label: 'Self Transfer Applications',
                    route: routes.selfTransfer.list,
                    slug: 'self_transfer'
                },
                {
                    label: 'Internal Vacancy (DMRC/Outstation/Vigilance/Misc)',
                    route: routes.transferPosting.internalVacancy,
                    slug: 'internal_vacancy_dmrc_outstation_vigilance_misc',
                    disabled: true
                },
                {
                    label: 'Mutual Transfer Applications',
                    route: routes.transferPosting.mutualTransfer,
                    slug: 'mutual_transfer',
                    disabled: true
                },
                {
                    label: 'Transfer Cancellation Representations',
                    route: routes.transferPosting.cancellation,
                    slug: 'transfer_cancellation',
                    disabled: true
                },
                {
                    label: 'Request for Posting',
                    route: routes.transferPosting.requestPosting,
                    slug: 'request_for_posting',
                    disabled: true
                },
                {
                    label: 'Download Office Order of Transfer',
                    route: routes.transferPosting.officeOrders,
                    slug: 'download_office_order_of_transfer',
                    disabled: true
                }
            ]
        },
        {
            id: 'other-hr',
            title: 'Other HR Services',
            description: 'Annual Property Return, VPF contribution, appeal for contribution, and E-Attestation process.',
            icon: 'ti ti-heart-handshake',
            items: [
                {
                    label: 'Annual Property Return',
                    route: routes.annualPropertyReturn,
                    slug: 'annual_property_return_submission'
                },
                {
                    label: 'VPF Contribution Module',
                    route: routes.vpfModule.list,
                    slug: 'employee_vpf_contribution_module'
                },
                {
                    label: 'Contribution On Appeal',
                    route: routes.appealContribution.list,
                    slug: 'appeal_for_contribution'
                },
                {
                    label: 'E-Attestation Process',
                    route: routes.eAttestation.list,
                    slug: 'e_attestation_process'
                },
                {
                    label: 'Booking of Community Hall/Common Room/Park',
                    route: routes.communityHall.list,
                    slug: 'booking_of_community_hall_common_room_park'
                },
                {
                    label: 'Change Password',
                    route: routes.changePassword,
                    slug: 'change_password'
                }
            ]
        }
    ];

    const filteredCategories = serviceCategories
        .map((category) => {
            // Category-level module status check
            if (category.slug && !isSlugActive(category.slug)) {
                return null;
            }

            // Sub-item module status check
            const activeItems = category.items.filter((item) => !item.slug || isSlugActive(item.slug));
            if (activeItems.length === 0) {
                return null;
            }

            const query = searchQuery.toLowerCase().trim();
            if (!query) {
                return {
                    ...category,
                    items: activeItems,
                };
            }

            const matchesCategory =
                category.title.toLowerCase().includes(query) ||
                category.description.toLowerCase().includes(query);

            const searchFilteredItems = activeItems.filter((item) =>
                item.label.toLowerCase().includes(query)
            );

            if (matchesCategory) {
                return {
                    ...category,
                    items: activeItems,
                };
            }
            if (searchFilteredItems.length > 0) {
                return {
                    ...category,
                    items: searchFilteredItems,
                };
            }
            return null;
        })
        .filter(Boolean) as ServiceCategory[];

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Page Header */}
                <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
                    <div className="my-auto mb-2">
                        <h3 className="page-title mb-1">Employee Services</h3>
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item">
                                    <Link to={routes.adminDashboard}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    All Services
                                </li>
                            </ol>
                        </nav>
                    </div>
                    {/* Search Bar */}
                    <div className="d-flex align-items-center mt-md-0 mt-2" style={{ maxWidth: "320px", width: "100%" }}>
                        <div className="input-group">
                            <span className="input-group-text bg-white border-end-0">
                                <i className="ti ti-search text-muted"></i>
                            </span>
                            <input
                                type="text"
                                className="form-control border-start-0 ps-0"
                                placeholder="Search services..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button
                                    className="btn btn-outline-secondary border-start-0"
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                >
                                    <i className="ti ti-x"></i>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="row g-4">
                    {filteredCategories.length > 0 ? (
                        filteredCategories.map((category) => (
                            <div key={category.id} className="col-xl-6 col-md-12">
                                <div className="card border-0 shadow-sm h-100 overflow-hidden">
                                    <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="avatar avatar-lg bg-danger-transparent rounded-3">
                                                <i className={`${category.icon} fs-24 text-danger`}></i>
                                            </div>
                                            <div>
                                                <h5 className="card-title mb-1 fw-bold">{category.title}</h5>
                                                <p className="small text-muted mb-0">{category.description}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body p-4 pt-4">
                                        <div className="row g-2">
                                            {category.items.map((item, idx) => (
                                                <div key={idx} className="col-md-6">
                                                    <Link 
                                                        to={item.disabled ? "#" : item.route} 
                                                        style={item.disabled ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
                                                        onClick={(e) => {
                                                            if (item.disabled) {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                        className={`d-flex justify-content-between align-items-center p-3 rounded-3 bg-light text-decoration-none transition-all ${item.disabled ? "text-muted" : "hover-white-shadow border border-transparent hover-border-danger"}`}
                                                    >
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className="small fw-semibold text-dark">{item.label}</span>
                                                            {item.disabled && (
                                                                <span className="badge bg-white text-muted border fs-10 py-0.5 px-1.5" style={{ fontSize: "10px" }}>
                                                                    Disabled
                                                                </span>
                                                            )}
                                                        </div>
                                                        <i className={`ti ${item.disabled ? "ti-ban text-muted" : "ti-chevron-right text-muted"} fs-12`}></i>
                                                    </Link>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="col-12 text-center py-5">
                            <i className="ti ti-search-off fs-40 text-muted mb-2"></i>
                            <h5 className="text-muted">No services found matching "{searchQuery}"</h5>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                .hover-white-shadow:hover {
                    background-color: #fff !important;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
                }
                .hover-border-danger:hover {
                    border-color: #A93226 !important;
                }
                .bg-danger-transparent { background-color: rgba(169, 50, 38, 0.1); }
                .transition-all { transition: all 0.25s ease-in-out; }
                .fs-12 { font-size: 12px; }
            `}</style>
        </div>
    );
};

export default Services;
